import type { Metrics, Pt } from '../metrics';
import type { Anchor, Dimension, DimensionId } from '../types';

type PointAnchor = { pointSourcePx: Pt };
type ItemAnchor = { itemId: string };
type RawAnchor = PointAnchor | ItemAnchor;

export interface DimensionView {
  id: DimensionId;
  a: Pt | null;
  b: Pt | null;
  aItemId: string | null;
  bItemId: string | null;
  offsetPx: number;
  valueMm: number | null;
  labelOverride: string | null;
  label: string | null;
  locked: boolean;
}

export type SerializedDimensions = Dimension[];
export type DimensionsSnapshot = Dimension[];

export interface DimensionStore {
  add(a: Anchor, b: Anchor, offsetPx: number): DimensionId;
  remove(id: DimensionId): void;
  setOffset(id: DimensionId, offsetPx: number): void;
  setLabelOverride(id: DimensionId, text: string | null): void;
  setLocked(id: DimensionId, locked: boolean): void;

  list(): DimensionView[];
  getMm(id: DimensionId): number | null;

  serialize(): SerializedDimensions;
  load(data: SerializedDimensions): void;

  snapshot(): DimensionsSnapshot;
  restore(s: DimensionsSnapshot): void;
}

interface DimensionStoreOptions {
  resolveItemAnchor?: (itemId: string) => Pt | null;
  formatLength?: (valueMm: number) => string;
}

function cloneAnchor(anchor: Anchor): Anchor {
  const raw = anchor as unknown as RawAnchor;
  if ('itemId' in raw) return anchorToItem(raw.itemId);
  return anchorToPoint(raw.pointSourcePx);
}

function cloneDimension(dimension: Dimension): Dimension {
  return {
    id: dimension.id,
    a: cloneAnchor(dimension.a),
    b: cloneAnchor(dimension.b),
    offsetPx: dimension.offsetPx,
    labelOverride: dimension.labelOverride ?? null,
    locked: Boolean(dimension.locked),
  };
}

function pointFromAnchor(anchor: Anchor, resolveItemAnchor?: (itemId: string) => Pt | null): Pt | null {
  const raw = anchor as unknown as RawAnchor;
  if ('itemId' in raw) return resolveItemAnchor?.(raw.itemId) ?? null;
  return 'pointSourcePx' in raw ? { ...raw.pointSourcePx } : null;
}

function itemIdFromAnchor(anchor: Anchor): string | null {
  const raw = anchor as unknown as RawAnchor;
  return 'itemId' in raw ? raw.itemId : null;
}

function labelFor(valueMm: number | null, labelOverride: string | null, formatLength?: (valueMm: number) => string): string | null {
  if (labelOverride) return labelOverride;
  return valueMm === null ? null : formatLength?.(valueMm) ?? `${Math.round(valueMm)} mm`;
}

function updateDimension(
  dimensions: Dimension[],
  id: DimensionId,
  update: (dimension: Dimension) => Dimension,
): Dimension[] {
  return dimensions.map(dimension => dimension.id === id ? update(dimension) : dimension);
}

export function anchorToItem(itemId: string): Anchor {
  return { itemId } as unknown as Anchor;
}

export function anchorToPoint(sourcePt: Pt): Anchor {
  return { pointSourcePx: { ...sourcePt } } as unknown as Anchor;
}

export function createDimensionStore(metrics: Metrics, options: DimensionStoreOptions = {}): DimensionStore {
  let dimensions: Dimension[] = [];

  const distanceFor = (dimension: Dimension): number | null => {
    const a = pointFromAnchor(dimension.a, options.resolveItemAnchor);
    const b = pointFromAnchor(dimension.b, options.resolveItemAnchor);
    return a && b ? metrics.distanceMm(a, b) : null;
  };

  return {
    add: (a, b, offsetPx) => {
      const id = crypto.randomUUID();
      dimensions = [
        ...dimensions,
        { id, a: cloneAnchor(a), b: cloneAnchor(b), offsetPx, labelOverride: null, locked: false },
      ];
      return id;
    },
    remove: id => {
      dimensions = dimensions.filter(dimension => dimension.id !== id);
    },
    setOffset: (id, offsetPx) => {
      dimensions = updateDimension(dimensions, id, dimension => ({ ...dimension, offsetPx }));
    },
    setLabelOverride: (id, text) => {
      dimensions = updateDimension(dimensions, id, dimension => ({ ...dimension, labelOverride: text }));
    },
    setLocked: (id, locked) => {
      dimensions = updateDimension(dimensions, id, dimension => ({ ...dimension, locked }));
    },
    list: () => dimensions.map(dimension => {
      const valueMm = distanceFor(dimension);
      const labelOverride = dimension.labelOverride ?? null;
      return {
        id: dimension.id,
        a: pointFromAnchor(dimension.a, options.resolveItemAnchor),
        b: pointFromAnchor(dimension.b, options.resolveItemAnchor),
        aItemId: itemIdFromAnchor(dimension.a),
        bItemId: itemIdFromAnchor(dimension.b),
        offsetPx: dimension.offsetPx,
        valueMm,
        labelOverride,
        label: labelFor(valueMm, labelOverride, options.formatLength),
        locked: Boolean(dimension.locked),
      };
    }),
    getMm: id => {
      const dimension = dimensions.find(candidate => candidate.id === id);
      return dimension ? distanceFor(dimension) : null;
    },
    serialize: () => dimensions.map(cloneDimension),
    load: data => {
      dimensions = data.map(cloneDimension);
    },
    snapshot: () => dimensions.map(cloneDimension),
    restore: snapshot => {
      dimensions = snapshot.map(cloneDimension);
    },
  };
}

export type { Anchor, DimensionId };
